import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  getOrdersBySeller, 
  getSellerStats, 
  getUnreadCountsBySeller,
  getSellerProfile,
  updateSellerProfile,
  getMyListings,  // Use the new cached function
  forceRefresh,    // For manual refresh
  clearCacheByPattern  // For cache management
} from '../services/api';

export const useSellerData = () => {
  const [listings, setListings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [sellerStats, setSellerStats] = useState({
    total_listings: 0,
    active_listings: 0,
    total_orders: 0,
    pending_orders: 0,
    total_revenue: 0,
    store_views: 0,
    followers: 0,
    conversion_rate: 0,
    today_orders: 0,
    revenue_trend: 0
  });
  const [sellerProfile, setSellerProfile] = useState(null);
  const [orderUnreadCounts, setOrderUnreadCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const { user, token } = useAuth();

  // Remove the old authenticatedApiCall function since your new API handles auth automatically

  const fetchMyListings = async (forceRefreshData = false) => {
    try {
      // Use the cached API function with force refresh option
      const response = await getMyListings({ 
        forceRefresh: forceRefreshData 
      });
      setListings(response.data || []);
      setError(null);
      return response.data || [];
    } catch (err) {
      const errorMessage = 'Failed to load listings: ' + err.message;
      setError(errorMessage);
      // console.error('fetchMyListings error:', err);
      throw err;
    }
  };

  const fetchSellerProfile = async (forceRefreshData = false) => {
    if (!user?.user_id) return;
    
    try {
      const response = await getSellerProfile(user.user_id, { 
        forceRefresh: forceRefreshData 
      });
      
      if (response.data && response.data.seller) {
        // Format the seller profile data
        const profile = {
          ...response.data.seller,
          store_name: response.data.seller.store_name || 'My Store',
          description: response.data.seller.store_description || 'Add a store description to attract more customers',
          location: response.data.seller.store_location || 'Add location',
          logo_url: response.data.seller.store_logo_url || null,
          cover_photo_url: response.data.seller.store_cover_picture_url || null,
          rating_avg: parseFloat(response.data.seller.rating_avg) || 0.0,
          total_sales: response.data.seller.total_sales || 0,
          category: response.data.seller.category || 'General',
          status: response.data.seller.status || 'inactive',
          is_approved: response.data.seller.is_approved || false,
          created_at: response.data.seller.created_at || new Date().toISOString()
        };
        
        setSellerProfile(profile);
      } else {
        // If no seller profile found, set empty profile
        setSellerProfile({
          store_name: 'My Store',
          description: 'Add a store description to attract more customers',
          location: 'Add location',
          logo_url: null,
          cover_photo_url: null,
          rating_avg: 0.0,
          total_sales: 0,
          category: 'General',
          status: 'inactive',
          is_approved: false,
          created_at: new Date().toISOString()
        });
      }
      setError(null);
      
      return sellerProfile;
    } catch (error) {
      // console.error('Error fetching seller profile:', error);
      // Set default profile on error
      setSellerProfile({
        store_name: 'My Store',
        description: 'Add a store description to attract more customers',
        location: 'Add location',
        logo_url: null,
        cover_photo_url: null,
        rating_avg: 0.0,
        total_sales: 0,
        category: 'General',
        status: 'inactive',
        is_approved: false,
        created_at: new Date().toISOString()
      });
      return null;
    }
  };

  const fetchSellerStats = async (forceRefreshData = false) => {
    if (!user?.user_id) return;
    
    try {
      const response = await getSellerStats(user.user_id, { 
        forceRefresh: forceRefreshData 
      });
      
      if (response.success !== false) {
        // Format stats with additional metrics for web
        const formattedStats = {
          total_listings: response.total_listings || 0,
          active_listings: response.active_listings || 0,
          total_orders: response.total_orders || 0,
          pending_orders: response.pending_orders || 0,
          total_revenue: response.total_revenue || 0,
          store_views: response.store_views || 0,
          followers: response.followers || 0,
          conversion_rate: response.conversion_rate || 0,
          today_orders: response.today_orders || 0,
          revenue_trend: response.revenue_trend || 0,
          // Additional stats for web
          average_rating: parseFloat(response.average_rating) || 0.0,
          total_customers: response.total_customers || 0,
          refund_rate: response.refund_rate || 0,
          average_order_value: response.average_order_value || 0
        };
        
        setSellerStats(formattedStats);
      }
      setError(null);
      
      return sellerStats;
    } catch (error) {
      // console.error('Error fetching seller stats:', error);
      // Return default stats on error
      return sellerStats;
    }
  };

  const fetchOrderUnreadCounts = async () => {
    if (!user?.user_id) return;
    
    try {
      // This is a POST endpoint, so it's not cached
      const response = await getUnreadCountsBySeller(user.user_id);
      setOrderUnreadCounts(response.unread_counts || {});
      setError(null);
      return response.unread_counts || {};
    } catch (error) {
      // console.error('Error fetching order unread counts:', error);
      return {};
    }
  };

  const fetchSellerOrders = async (forceRefreshData = false) => {
    try {
      const response = await getOrdersBySeller(user.user_id, { 
        forceRefresh: forceRefreshData 
      });
      const ordersData = response.data || [];
      
      // Format orders with additional data for web
      const formattedOrders = ordersData.map(order => ({
        ...order,
        customer_name: order.buyer_name || `Customer #${order.buyer_id}`,
        customer_email: order.buyer_email || 'N/A',
        total_amount: parseFloat(order.total_amount) || 0,
        created_at: order.created_at || new Date().toISOString(),
        status: order.status || 'pending',
        items_count: order.items?.length || 0,
        shipping_address: order.shipping_address || 'N/A'
      }));
      
      setOrders(formattedOrders);
      setError(null);
      return formattedOrders;
    } catch (err) {
      const errorMessage = 'Failed to load orders: ' + err.message;
      setError(errorMessage);
      // console.error('fetchSellerOrders error:', err);
      throw err;
    }
  };

  const fetchData = async (forceReload = false, showAlert = false) => {
    if (!user?.user_id || !user?.is_seller) {
      setLoading(false);
      return;
    }
    
    setRefreshing(true);
    setError(null);
    
    try {
      await Promise.all([
        fetchMyListings(forceReload),
        fetchSellerOrders(forceReload),
        fetchSellerProfile(forceReload),
        fetchSellerStats(forceReload),
        fetchOrderUnreadCounts()  // This one is not cached
      ]);
      
      if (showAlert) {
        // Use a toast notification system in production
        // console.log('Data refreshed successfully');
        // You can integrate with a toast library like react-toastify
        // toast.success('Data refreshed successfully');
      }
    } catch (error) {
      // console.error('Error fetching seller data:', error);
      
      if (showAlert) {
        // console.error('Failed to refresh data: ' + error.message);
        // toast.error('Failed to refresh data');
      }
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  };

  const refreshData = () => {
    // Clear cache before refreshing
    clearCacheByPattern('/my-listings');
    clearCacheByPattern('/orders/seller/');
    clearCacheByPattern('/sellers/');
    clearCacheByPattern('/seller/stats');
    
    fetchData(true, true);
  };

  const handleUpdateSellerProfile = async (userId, updateData) => {
    try {
      setLoading(true);
      const result = await updateSellerProfile(userId, updateData);
      
      // Update local state with the returned data
      if (result.data) {
        const updatedProfile = {
          ...sellerProfile,
          ...result.data,
          store_name: result.data.store_name || sellerProfile?.store_name,
          description: result.data.store_description || sellerProfile?.description,
          location: result.data.store_location || sellerProfile?.location,
          logo_url: result.data.store_logo_url || sellerProfile?.logo_url,
          cover_photo_url: result.data.store_cover_picture_url || sellerProfile?.cover_photo_url
        };
        
        setSellerProfile(updatedProfile);
      }
      
      // Clear seller cache after update
      clearCacheByPattern('/sellers/');
      clearCacheByPattern('/seller/profile');
      
      return result;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Update listing status locally
  const updateListingStatus = (listingId, status) => {
    setListings(prevListings => 
      prevListings.map(listing => 
        listing.listing_id === listingId 
          ? { ...listing, status }
          : listing
      )
    );
    
    // Update stats if needed
    if (status === 'active' || status === 'inactive') {
      const activeCount = listings.filter(l => l.status === 'active').length;
      setSellerStats(prev => ({
        ...prev,
        active_listings: activeCount
      }));
    }
    
    // Clear listings cache
    clearCacheByPattern('/listings');
    clearCacheByPattern('/my-listings');
  };

  // Update order status locally
  const updateOrderStatus = (orderId, status) => {
    setOrders(prevOrders => 
      prevOrders.map(order => 
        order.order_id === orderId 
          ? { ...order, status }
          : order
      )
    );
    
    // Update stats if needed
    if (status === 'pending' || status === 'completed' || status === 'cancelled') {
      const pendingCount = orders.filter(o => o.status === 'pending').length;
      setSellerStats(prev => ({
        ...prev,
        pending_orders: pendingCount
      }));
    }
    
    // Clear orders cache
    clearCacheByPattern('/orders/');
  };

  // Remove listing locally
  const removeListing = (listingId) => {
    setListings(prevListings => 
      prevListings.filter(listing => listing.listing_id !== listingId)
    );
    
    // Update stats
    const total = listings.length - 1;
    const active = listings.filter(l => l.status === 'active').length;
    setSellerStats(prev => ({
      ...prev,
      total_listings: total,
      active_listings: active
    }));
    
    // Clear listings cache
    clearCacheByPattern('/listings');
    clearCacheByPattern('/my-listings');
  };

  // Add new listing
  const addListing = (newListing) => {
    setListings(prev => [newListing, ...prev]);
    
    // Update stats
    const total = listings.length + 1;
    const active = [...listings, newListing].filter(l => l.status === 'active').length;
    setSellerStats(prev => ({
      ...prev,
      total_listings: total,
      active_listings: active
    }));
    
    // Clear listings cache
    clearCacheByPattern('/listings');
    clearCacheByPattern('/my-listings');
  };

  // Add new order
  const addOrder = (newOrder) => {
    setOrders(prev => [newOrder, ...prev]);
    
    // Update stats
    const total = orders.length + 1;
    const pending = [...orders, newOrder].filter(o => o.status === 'pending').length;
    setSellerStats(prev => ({
      ...prev,
      total_orders: total,
      pending_orders: pending
    }));
    
    // Clear orders cache
    clearCacheByPattern('/orders/');
  };

  // Mark order as read
  const markOrderAsRead = (orderId) => {
    setOrderUnreadCounts(prev => ({
      ...prev,
      [orderId]: 0
    }));
  };

  // Get unread count for specific order
  const getOrderUnreadCount = (orderId) => {
    return orderUnreadCounts[orderId] || 0;
  };

  // Get total unread messages
  const getTotalUnreadMessages = () => {
    return Object.values(orderUnreadCounts).reduce((sum, count) => sum + count, 0);
  };

  // Get listing by ID
  const getListingById = (listingId) => {
    return listings.find(listing => listing.listing_id === listingId);
  };

  // Get order by ID
  const getOrderById = (orderId) => {
    return orders.find(order => order.order_id === orderId);
  };

  // Filter listings by status
  const getListingsByStatus = (status) => {
    return listings.filter(listing => listing.status === status);
  };

  // Filter orders by status
  const getOrdersByStatus = (status) => {
    return orders.filter(order => order.status === status);
  };

  // Load data with optional caching
  const loadWithCache = async (force = false) => {
    return fetchData(force);
  };

  useEffect(() => {
    if (user?.user_id && user?.is_seller) {
      // Initial load - use cache if available
      fetchData(false);
    } else {
      setLoading(false);
    }
  }, [user?.user_id, user?.is_seller]);

  return {
    // Data
    listings,
    orders,
    sellerStats,
    sellerProfile,
    orderUnreadCounts,
    
    // State
    loading,
    refreshing,
    error,
    
    // Data fetching
    fetchData,
    refreshData,
    loadWithCache,
    fetchMyListings,
    fetchSellerOrders,
    fetchSellerProfile,
    fetchSellerStats,
    fetchOrderUnreadCounts,
    
    // Data manipulation
    setListings,
    setOrders,
    handleUpdateSellerProfile,
    updateListingStatus,
    updateOrderStatus,
    removeListing,
    addListing,
    addOrder,
    markOrderAsRead,
    
    // Utility functions
    getListingById,
    getOrderById,
    getListingsByStatus,
    getOrdersByStatus,
    getOrderUnreadCount,
    getTotalUnreadMessages,
    
    // Cache management
    clearCacheByPattern
  };
};